import { useState, useEffect } from 'react';
import { 
  Plus, Pencil, Trash2, Eye, EyeOff, Save, X, 
  UtensilsCrossed, Image, ArrowLeft, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuCategories = [
  { id: 'malaysian', label: 'Malaysian Brunch' },
  { id: 'flaguette', label: 'Signature Flaguette' },
  { id: 'pastries', label: 'Fresh Pastries' },
  { id: 'gelato', label: 'Artisan Gelato' },
  { id: 'extras', label: 'Extras' },
];

const galleryCategories = [
  { id: 'food', label: 'Food' },
  { id: 'drinks', label: 'Drinks' },
  { id: 'pastry', label: 'Pastries' },
  { id: 'dessert', label: 'Desserts' },
  { id: 'cafe', label: 'Cafe' },
];

export default function AdminCMSPage() {
  const [activeTab, setActiveTab] = useState('menu');
  const [menuItems, setMenuItems] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [menuRes, galleryRes] = await Promise.all([
        axios.get(`${API}/menu-items`),
        axios.get(`${API}/gallery-images/all`)
      ]);
      setMenuItems(menuRes.data || []);
      setGalleryImages(galleryRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Menu Functions
  const openMenuModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description,
        price: item.price,
        category: item.category,
        image_url: item.image_url || '',
        tags: item.tags?.join(', ') || '',
        is_available: item.is_available
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        category: 'malaysian',
        image_url: '',
        tags: '',
        is_available: true
      });
    }
    setShowModal('menu');
  };

  const saveMenuItem = async () => {
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : []
      };

      if (editingItem) {
        await axios.put(`${API}/menu-items/${editingItem.id}`, payload);
      } else {
        await axios.post(`${API}/menu-items`, payload);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving menu item:', error);
      alert('Failed to save menu item');
    }
  };

  const deleteMenuItem = async (id) => {
    if (!window.confirm('Delete this menu item?')) return;
    try {
      await axios.delete(`${API}/menu-items/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting menu item:', error);
    }
  };

  const toggleMenuAvailability = async (id) => {
    try {
      await axios.patch(`${API}/menu-items/${id}/availability`);
      fetchData();
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  // Gallery Functions
  const openGalleryModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        image_url: item.image_url,
        alt_text: item.alt_text,
        category: item.category
      });
    } else {
      setEditingItem(null);
      setFormData({
        image_url: '',
        alt_text: '',
        category: 'food'
      });
    }
    setShowModal('gallery');
  };

  const saveGalleryImage = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/gallery-images/${editingItem.id}`, formData);
      } else {
        await axios.post(`${API}/gallery-images`, formData);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving gallery image:', error);
      alert('Failed to save gallery image');
    }
  };

  const deleteGalleryImage = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await axios.delete(`${API}/gallery-images/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting gallery image:', error);
    }
  };

  const toggleGalleryVisibility = async (id) => {
    try {
      await axios.patch(`${API}/gallery-images/${id}/visibility`);
      fetchData();
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  return (
    <div className="min-h-screen pt-24 bg-[#FAFAF5]" data-testid="admin-cms-page">
      {/* Header */}
      <div className="bg-black text-white py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex justify-between items-center">
            <div>
              <Link to="/admin" className="text-neutral-400 text-sm flex items-center gap-2 mb-2 hover:text-white">
                <ArrowLeft size={16} /> Back to Dashboard
              </Link>
              <h1 className="font-serif text-3xl font-bold">Content Manager</h1>
              <p className="text-neutral-400 text-sm mt-1">Manage your menu and gallery</p>
            </div>
            <button
              onClick={fetchData}
              disabled={isLoading}
              className="flex items-center gap-2 bg-white/10 px-4 py-2 text-sm hover:bg-white/20 transition-colors"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-6">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('menu')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
              activeTab === 'menu' ? 'bg-black text-white' : 'bg-white hover:shadow-md'
            }`}
          >
            <UtensilsCrossed size={18} />
            Menu ({menuItems.length})
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
              activeTab === 'gallery' ? 'bg-black text-white' : 'bg-white hover:shadow-md'
            }`}
          >
            <Image size={18} />
            Gallery ({galleryImages.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl pb-16">
        {/* Menu Tab */}
        {activeTab === 'menu' && (
          <div className="bg-white p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl font-semibold">Menu Items</h2>
              <button
                onClick={() => openMenuModal()}
                className="flex items-center gap-2 bg-black text-white px-4 py-2 hover:bg-neutral-800"
              >
                <Plus size={18} /> Add Menu Item
              </button>
            </div>

            {menuCategories.map((cat) => {
              const categoryItems = menuItems.filter(item => item.category === cat.id);
              if (categoryItems.length === 0) return null;
              
              return (
                <div key={cat.id} className="mb-8">
                  <h3 className="font-semibold text-lg mb-4 pb-2 border-b">{cat.label}</h3>
                  <div className="space-y-3">
                    {categoryItems.map((item) => (
                      <div key={item.id} className={`flex items-center justify-between p-4 border ${!item.is_available ? 'opacity-50 bg-neutral-50' : ''}`}>
                        <div className="flex items-center gap-4">
                          {item.image_url && (
                            <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold">{item.name}</h4>
                              {item.tags?.map((tag, i) => (
                                <span key={i} className="text-xs bg-neutral-100 px-2 py-0.5 rounded">{tag}</span>
                              ))}
                              {!item.is_available && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Unavailable</span>}
                            </div>
                            <p className="text-sm text-neutral-500 mt-1">{item.description}</p>
                            <p className="font-semibold mt-1">£{item.price}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => toggleMenuAvailability(item.id)} className="p-2 hover:bg-neutral-100" title="Toggle availability">
                            {item.is_available ? <Eye size={18} /> : <EyeOff size={18} />}
                          </button>
                          <button onClick={() => openMenuModal(item)} className="p-2 hover:bg-neutral-100" title="Edit">
                            <Pencil size={18} />
                          </button>
                          <button onClick={() => deleteMenuItem(item.id)} className="p-2 hover:bg-red-100 text-red-600" title="Delete">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {menuItems.length === 0 && (
              <div className="text-center py-12 text-neutral-500">
                <p>No menu items yet. Click "Add Menu Item" to get started.</p>
              </div>
            )}
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="bg-white p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl font-semibold">Gallery Images</h2>
              <button
                onClick={() => openGalleryModal()}
                className="flex items-center gap-2 bg-black text-white px-4 py-2 hover:bg-neutral-800"
              >
                <Plus size={18} /> Add Image
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {galleryImages.map((image) => (
                <div key={image.id} className={`relative group ${!image.is_visible ? 'opacity-50' : ''}`}>
                  <img
                    src={image.image_url}
                    alt={image.alt_text}
                    className="w-full aspect-square object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button onClick={() => toggleGalleryVisibility(image.id)} className="p-2 bg-white rounded hover:bg-neutral-100" title="Toggle visibility">
                      {image.is_visible ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>
                    <button onClick={() => openGalleryModal(image)} className="p-2 bg-white rounded hover:bg-neutral-100" title="Edit">
                      <Pencil size={18} />
                    </button>
                    <button onClick={() => deleteGalleryImage(image.id)} className="p-2 bg-white rounded hover:bg-red-100 text-red-600" title="Delete">
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <p className="text-xs text-neutral-500 mt-1 truncate">{image.alt_text}</p>
                  <span className="text-xs bg-neutral-100 px-2 py-0.5 rounded">{image.category}</span>
                </div>
              ))}
            </div>

            {galleryImages.length === 0 && (
              <div className="text-center py-12 text-neutral-500">
                <p>No gallery images yet. Click "Add Image" to get started.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-serif text-xl font-semibold">
                {showModal === 'menu' 
                  ? (editingItem ? 'Edit Menu Item' : 'Add Menu Item')
                  : (editingItem ? 'Edit Gallery Image' : 'Add Gallery Image')
                }
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-neutral-100 rounded">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {showModal === 'menu' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full border p-2"
                      placeholder="e.g., Nasi Lemak"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description *</label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full border p-2"
                      rows={3}
                      placeholder="Describe the dish..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Price (£) *</label>
                      <input
                        type="text"
                        value={formData.price || ''}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        className="w-full border p-2"
                        placeholder="11.95"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Category *</label>
                      <select
                        value={formData.category || 'malaysian'}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full border p-2"
                      >
                        {menuCategories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Image URL (optional)</label>
                    <input
                      type="text"
                      value={formData.image_url || ''}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      className="w-full border p-2"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
                    <input
                      type="text"
                      value={formData.tags || ''}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                      className="w-full border p-2"
                      placeholder="FAMILY RECIPE, GF, V"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_available"
                      checked={formData.is_available !== false}
                      onChange={(e) => setFormData({...formData, is_available: e.target.checked})}
                    />
                    <label htmlFor="is_available" className="text-sm">Available on menu</label>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Image URL *</label>
                    <input
                      type="text"
                      value={formData.image_url || ''}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      className="w-full border p-2"
                      placeholder="https://..."
                    />
                    {formData.image_url && (
                      <img src={formData.image_url} alt="Preview" className="mt-2 w-32 h-32 object-cover" />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Caption / Alt Text *</label>
                    <input
                      type="text"
                      value={formData.alt_text || ''}
                      onChange={(e) => setFormData({...formData, alt_text: e.target.value})}
                      className="w-full border p-2"
                      placeholder="e.g., Nasi Lemak with sambal"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Category *</label>
                    <select
                      value={formData.category || 'food'}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full border p-2"
                    >
                      {galleryCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <button
                onClick={showModal === 'menu' ? saveMenuItem : saveGalleryImage}
                className="w-full flex items-center justify-center gap-2 bg-black text-white py-3 font-medium hover:bg-neutral-800"
              >
                <Save size={18} />
                {editingItem ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
